import React, { useEffect, useRef, useState } from "react";
import { Download, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useEa } from "@/components/energy-analytics/analytics/EaContext";
import { inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { MONTHS_IT, SCOPE_LABEL } from "@/components/energy-analytics/analytics/eaFormat";

export function EffValues({ data, year, reload }) {
  const { filters } = useEa();
  const params = { company_id: filters.company || undefined };
  const [siteId, setSiteId] = useState(data.sites[0]?.id || "");
  const [grid, setGrid] = useState({});
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const fileRef = useRef(null);
  const site = data.sites.find((s) => s.id === siteId);

  useEffect(() => {
    if (!siteId) return undefined;
    let alive = true;
    setGrid({});
    api.get("/ea/efficiency/values", { params: { ...params, site_id: siteId, year } }).then((r) => {
      if (alive) setGrid(Object.fromEntries(r.data.values.map((v) => [`${v.driver_id}-${v.month}`, String(v.value).replace(".", ",")])));
    });
    return () => { alive = false; };
  }, [siteId, year, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    const values = site.drivers.flatMap((d) => MONTHS_IT.map((_, i) => {
      const t = (grid[`${d.id}-${i + 1}`] || "").trim();
      const raw = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;  // 1.234,5 (it) oppure 1234.5
      return { driver_id: d.id, month: i + 1, value: raw === "" ? null : Number(raw) };
    }));
    const bad = values.filter((v) => v.value !== null && (Number.isNaN(v.value) || v.value < 0));
    if (bad.length) {
      setBusy(false);
      return toast.error(`Valori non validi (solo numeri ≥ 0): ${bad.map((v) => `${site.drivers.find((d) => d.id === v.driver_id).name} ${MONTHS_IT[v.month - 1]}`).join(", ")}`);
    }
    try { await api.put("/ea/efficiency/values", { site_id: siteId, year, values }, { params }); toast.success("Valori salvati"); reload(); }
    catch { toast.error("Salvataggio non riuscito"); } finally { setBusy(false); }
  };
  const template = async () => {
    const r = await api.get("/ea/efficiency/template.xlsx", { params: { ...params, year }, responseType: "blob" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: `driver_efficienza_${year}.xlsx` });
    a.click();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/ea/efficiency/upload", fd, { params: { ...params, year } });
      toast.success(`Aggiornati ${r.data.saved} valori${r.data.skipped_rows ? ` · ${r.data.skipped_rows} righe ignorate` : ""}`);
      setRefresh((x) => x + 1); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import non riuscito"); }
    e.target.value = "";
  };
  if (!data.sites.length) return <div className="text-sm text-slate-500" data-testid="eff-values-empty">Crea prima un ambito (totale aziendale, sito o POD/PDR) con i suoi driver nella scheda "Ambiti e driver".</div>;
  return (
    <div className="space-y-4" data-testid="eff-values">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className={inputCls} data-testid="eff-values-site">
          {data.sites.map((s) => <option key={s.id} value={s.id}>{SCOPE_LABEL[s.scope]} · {s.name}</option>)}
        </select>
        <span className="text-sm text-slate-500">Anno {year}</span>
        <div className="flex-1" />
        <button onClick={template} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2" data-testid="eff-template"><Download size={15} /> Scarica modello Excel</button>
        <button onClick={() => fileRef.current?.click()} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2" data-testid="eff-upload"><Upload size={15} /> Carica Excel</button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="eff-upload-input" />
      </div>
      {site && !site.drivers.length ? <div className="text-sm text-slate-500">Questo ambito non ha driver: aggiungili nella scheda "Ambiti e driver".</div> : site && (
        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm" data-testid="eff-values-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2 text-left">Driver</th>{MONTHS_IT.map((m) => <th key={m} className="px-1 py-2">{m}</th>)}</tr>
            </thead>
            <tbody>
              {site.drivers.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{d.name} <span className="text-xs text-slate-400">({d.unit})</span></td>
                  {MONTHS_IT.map((m, i) => (
                    <td key={m} className="px-1 py-1">
                      <input inputMode="decimal" value={grid[`${d.id}-${i + 1}`] || ""} onChange={(e) => setGrid({ ...grid, [`${d.id}-${i + 1}`]: e.target.value })}
                        className="w-20 h-8 rounded border border-slate-200 px-1.5 text-right font-mono-nums text-xs" data-testid={`eff-value-${d.id}-${i + 1}`} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button onClick={save} disabled={busy} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid="eff-values-save"><Save size={15} /> Salva valori</button>
      <div className="text-xs text-slate-400">Lascia vuoto un mese senza dato. Nel modello Excel compila solo le colonne dei mesi, senza modificare le righe.</div>
    </div>
  );
}
